import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ProductCodesProvider } from './context/ProductCodesContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import LegalPage from './pages/LegalPage';
import ClientPortalLayout from './components/ClientPortalLayout';
import AIChatWidget from './components/AIChatWidget';
import { ADMIN_ROUTES } from './config/appRoutes';
import { PORTAL_ROUTES } from './config/portalRoutes';
import { RequirePermission } from './hooks/useCan';
import { userCan } from './config/permissions';
import { DrillDownProvider } from './components/DrillPanel';

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-zinc-400 text-sm animate-pulse">Loading...</div>
    </div>
  );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  const defaultRoute = user?.role === 'client' ? '/portal' : '/dashboard';
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={defaultRoute} replace /> : <Login />} />
      <Route path="/privacy" element={<LegalPage />} />
      <Route path="/terms" element={<LegalPage />} />
      <Route path="/" element={<Navigate to={defaultRoute} replace />} />

      {/* ═══ CLIENT PORTAL ═══ */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <ClientPortalLayout />
        </ProtectedRoute>
      }>
        {PORTAL_ROUTES.map(({ path, component: C, index }) => (
          index
            ? <Route key="portal-index" index element={<C />} />
            : <Route key={path} path={path} element={<C />} />
        ))}
      </Route>

      {/* ═══ ADMIN APP ═══ */}
      <Route path="/*" element={
        <ProtectedRoute>
          {user?.role === 'client' ? <Navigate to="/portal" replace /> : (
          <Layout>
            <Routes>
              {ADMIN_ROUTES.map(({ path, component: C, perm }) => {
                const element = perm
                  ? <RequirePermission perm={perm}><C /></RequirePermission>
                  : <C />;
                return <Route key={path} path={path} element={element} />;
              })}
            </Routes>
            {userCan(user, 'feature_ti_chat') && <AIChatWidget />}
          </Layout>
          )}
        </ProtectedRoute>
      } />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ProductCodesProvider>
          <BrowserRouter>
            <DrillDownProvider>
              <AppRoutes />
              <Toaster
                position="top-right"
                toastOptions={{
                  style: { background: '#18181B', border: '1px solid #27272A', color: '#FAFAFA' },
                  classNames: { success: 'border-green-700', error: 'border-red-700' },
                }}
              />
            </DrillDownProvider>
          </BrowserRouter>
        </ProductCodesProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
